"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { AgentsView } from "@/components/agents-view";
import { BrowserLabView } from "@/components/browser-lab-view";
import { GeneratorView } from "@/components/generator-view";
import { IntegrationsView } from "@/components/integrations-view";
import { LoginView } from "@/components/login-view";
import { OverviewView } from "@/components/overview-view";
import { OrdersView } from "@/components/orders-view";
import { RunsView } from "@/components/runs-view";
import { TestPipelineStrip } from "@/components/test-pipeline-strip";
import { ApiRequestError, clearStoredCsrfToken, loadCurrentUser, loadStoredCsrfToken, login, logout, type AdminUser } from "@/lib/auth-api";
import { demoAgents, demoCases, demoOverview } from "@/lib/demo-data";
import {
    completePageTestRun,
    createPageTestRun,
    generatePageCases,
    loadAgents,
    loadLatestQualityAnalysis,
    loadOverview,
    saveQualityAnalysis,
} from "@/lib/qa-api";
import type {
    Agent,
    GeneratedCase,
    NavKey,
    PageTestSource,
    QualityAnalysisSaveInput,
    QualityAnalysisStage,
    QualityOverview,
    RunPhase,
    TestRun,
} from "@/lib/qa-types";

const navItems: { key: NavKey; label: string; glyph: string }[] = [
    { key: "browser", label: "质量探索", glyph: "◎" },
    { key: "generate", label: "场景生成", glyph: "✦" },
    { key: "runs", label: "任务执行", glyph: "▶" },
    { key: "overview", label: "质量总览", glyph: "⌁" },
    { key: "agents", label: "Agent 资源池", glyph: "▦" },
    { key: "orders", label: "订单中心", glyph: "▤" },
];

type QualityAnalysisSnapshot = Omit<QualityAnalysisSaveInput, "analysis_id" | "expected_version">;

const pipelineStages = new Set<NavKey>(["browser", "generate", "runs", "overview"]);

export function AppShell() {
    const [authChecking, setAuthChecking] = useState(true);
    const [authError, setAuthError] = useState<string | null>(null);
    const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
    const [csrfToken, setCsrfToken] = useState<string | null>(null);
    const [active, setActive] = useState<NavKey>("browser");
    const [overview, setOverview] = useState<QualityOverview>(demoOverview);
    const [agents, setAgents] = useState<Agent[]>(demoAgents);
    const [source, setSource] = useState<"live" | "demo">("demo");
    const [runPhase, setRunPhase] = useState<RunPhase>("idle");
    const [isGenerating, setIsGenerating] = useState(false);
    const [cases, setCases] = useState<GeneratedCase[]>([]);
    const [pipelineSource, setPipelineSource] = useState<PageTestSource | null>(null);
    const [currentRun, setCurrentRun] = useState<TestRun | null>(null);
    const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const accountMenuRef = useRef<HTMLDivElement>(null);
    const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
    const pipelineSequence = useRef(0);
    const analysisIdentity = useRef<{ analysisId: string; version: number } | null>(null);
    const saveQueue = useRef<Promise<void>>(Promise.resolve());

    const restoreLatestAnalysis = useCallback(async () => {
        try {
            const restored = await loadLatestQualityAnalysis();
            if (!restored) {
                analysisIdentity.current = null;
                return;
            }
            analysisIdentity.current = { analysisId: restored.analysis_id, version: restored.version };
            setPipelineSource(restored.source);
            setCases(restored.cases);
            setCurrentRun(restored.run);
            setRunPhase(restored.run_phase);
            if (restored.report) setOverview(restored.report.overview);
            setActive(restored.active_stage);
            setSaveState("saved");
        } catch {
            setSaveState("error");
        }
    }, []);

    useEffect(() => {
        void loadCurrentUser()
            .then(async (user) => {
                setCurrentUser(user);
                setCsrfToken(user ? loadStoredCsrfToken() : null);
                if (user) setActive("browser");
                if (!user) clearStoredCsrfToken();
                if (user) await restoreLatestAnalysis();
            })
            .catch((error: unknown) => setAuthError(authErrorMessage(error)))
            .finally(() => setAuthChecking(false));
        void Promise.all([loadOverview(), loadAgents()]).then(([overviewResult, agentResult]) => {
            setOverview(overviewResult.data);
            setAgents(agentResult.data);
            setSource(overviewResult.source === "live" && agentResult.source === "live" ? "live" : "demo");
        });
        return () => {
            pipelineSequence.current += 1;
            timers.current.forEach(clearTimeout);
        };
    }, [restoreLatestAnalysis]);

    useEffect(() => {
        if (!accountMenuOpen) return;

        function closeOnOutsideClick(event: PointerEvent) {
            if (!accountMenuRef.current?.contains(event.target as Node)) setAccountMenuOpen(false);
        }

        function closeOnEscape(event: KeyboardEvent) {
            if (event.key === "Escape") setAccountMenuOpen(false);
        }

        document.addEventListener("pointerdown", closeOnOutsideClick);
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.removeEventListener("pointerdown", closeOnOutsideClick);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [accountMenuOpen]);

    async function handleLogin(username: string, password: string) {
        setAuthError(null);
        try {
            const session = await login(username, password);
            setCsrfToken(session.csrf_token);
            setActive("browser");
            await restoreLatestAnalysis();
            setCurrentUser(session.user);
        } catch (error) {
            setAuthError(authErrorMessage(error));
        }
    }

    async function handleLogout() {
        setAuthError(null);
        setAccountMenuOpen(false);
        try {
            await logout(csrfToken);
        } catch (error) {
            setAuthError(authErrorMessage(error));
            return;
        }
        clearStoredCsrfToken();
        setCurrentUser(null);
        setCsrfToken(null);
        setPipelineSource(null);
        setCases([]);
        setCurrentRun(null);
        setRunPhase("idle");
        setSaveState("idle");
        analysisIdentity.current = null;
    }

    function queueAnalysisSave(snapshot: QualityAnalysisSnapshot): Promise<void> {
        if (!csrfToken) return Promise.resolve();
        setSaveState("saving");
        const queued = saveQueue.current.then(async () => {
            const identity = analysisIdentity.current;
            try {
                const saved = await saveQualityAnalysis(csrfToken, {
                    ...snapshot,
                    ...(identity ? { analysis_id: identity.analysisId, expected_version: identity.version } : {}),
                });
                analysisIdentity.current = { analysisId: saved.analysis_id, version: saved.version };
                setSaveState("saved");
            } catch {
                setSaveState("error");
            }
        });
        saveQueue.current = queued.catch(() => undefined);
        return queued;
    }

    function pipelineSnapshot(activeStage: QualityAnalysisStage): QualityAnalysisSnapshot | null {
        if (!pipelineSource) return null;
        return {
            active_stage: activeStage,
            run_phase: runPhase,
            source: pipelineSource,
            cases,
            run: currentRun,
            report: runPhase === "passed" ? { overview, case_count: cases.length, completed_at: new Date().toISOString() } : null,
        };
    }

    function navigateTo(target: NavKey) {
        setActive(target);
        if (!pipelineStages.has(target)) return;
        const snapshot = pipelineSnapshot(target as QualityAnalysisStage);
        if (snapshot) void queueAnalysisSave(snapshot);
    }

    function startRun() {
        timers.current.forEach(clearTimeout);
        setActive(active === "runs" ? "runs" : "overview");
        setRunPhase("queued");
        const sequence: [number, RunPhase][] = [
            [650, "running"],
            [1500, "healing"],
            [2600, "passed"],
        ];
        timers.current = sequence.map(([delay, phase]) => setTimeout(() => setRunPhase(phase), delay));
    }

    async function generateCases(requirement: string) {
        const source = pipelineSource ?? {
            target_url: "http://localhost:3000/",
            title: "手工需求场景",
            requirement,
            characteristics: ["来源为手工输入需求", "覆盖正向、逆向和边界场景"],
        };
        const updatedSource = { ...source, requirement: requirement.trim() };
        setPipelineSource(updatedSource);
        setIsGenerating(true);
        setCases([]);
        let generated: GeneratedCase[];
        try {
            const [result] = await Promise.all([
                generatePageCases(updatedSource),
                new Promise((resolve) => {
                    const timer = setTimeout(resolve, 350);
                    timers.current.push(timer);
                }),
            ]);
            generated = result;
            setCases(generated);
            void queueAnalysisSave({
                active_stage: "generate",
                run_phase: "idle",
                source: updatedSource,
                cases: generated,
                run: null,
                report: null,
            });
        } catch {
            generated = demoCases;
            setCases(generated);
            void queueAnalysisSave({
                active_stage: "generate",
                run_phase: "idle",
                source: updatedSource,
                cases: generated,
                run: null,
                report: null,
            });
        } finally {
            setIsGenerating(false);
        }
    }

    function acceptPageAnalysis(source: PageTestSource) {
        void runAutomatedPipeline(source);
    }

    async function runAutomatedPipeline(source: PageTestSource) {
        const sequence = pipelineSequence.current + 1;
        pipelineSequence.current = sequence;
        timers.current.forEach(clearTimeout);
        await saveQueue.current;
        analysisIdentity.current = null;
        setPipelineSource(source);
        setCases([]);
        setCurrentRun(null);
        setRunPhase("idle");
        setActive("generate");
        setIsGenerating(true);
        void queueAnalysisSave({
            active_stage: "generate",
            run_phase: "idle",
            source,
            cases: [],
            run: null,
            report: null,
        });

        let generated: GeneratedCase[];
        try {
            [generated] = await Promise.all([generatePageCases(source), waitForPipelineStage(450)]);
        } catch {
            generated = demoCases;
            await waitForPipelineStage(450);
        }
        if (pipelineSequence.current !== sequence) return;
        setCases(generated);
        setIsGenerating(false);
        void queueAnalysisSave({
            active_stage: "generate",
            run_phase: "idle",
            source,
            cases: generated,
            run: null,
            report: null,
        });

        await waitForPipelineStage(650);
        if (pipelineSequence.current !== sequence) return;
        setActive("runs");
        setRunPhase("queued");

        let created: TestRun;
        try {
            created = await createPageTestRun(
                source,
                generated.map((item) => item.case_id),
            );
        } catch {
            created = fallbackPipelineRun(source, generated, "RUNNING");
        }
        if (pipelineSequence.current !== sequence) return;
        setCurrentRun(created);
        void queueAnalysisSave({
            active_stage: "runs",
            run_phase: "running",
            source,
            cases: generated,
            run: created,
            report: null,
        });

        await waitForPipelineStage(450);
        if (pipelineSequence.current !== sequence) return;
        setRunPhase("running");
        await waitForPipelineStage(650);
        if (pipelineSequence.current !== sequence) return;
        setRunPhase("healing");
        await waitForPipelineStage(650);
        if (pipelineSequence.current !== sequence) return;

        let completed: TestRun;
        try {
            completed = await completePageTestRun(created.run_id);
        } catch {
            completed = { ...created, status: "PASSED" };
        }
        if (pipelineSequence.current !== sequence) return;
        setCurrentRun(completed);
        setRunPhase("passed");
        void queueAnalysisSave({
            active_stage: "overview",
            run_phase: "passed",
            source,
            cases: generated,
            run: completed,
            report: { overview, case_count: generated.length, completed_at: new Date().toISOString() },
        });

        await waitForPipelineStage(650);
        if (pipelineSequence.current === sequence) setActive("overview");
    }

    function waitForPipelineStage(delay: number): Promise<void> {
        return new Promise((resolve) => {
            const timer = setTimeout(resolve, delay);
            timers.current.push(timer);
        });
    }

    async function executeGeneratedCases() {
        if (!pipelineSource || cases.length === 0) return;
        timers.current.forEach(clearTimeout);
        setActive("runs");
        setCurrentRun(null);
        setRunPhase("queued");
        try {
            const created = await createPageTestRun(
                pipelineSource,
                cases.map((item) => item.case_id),
            );
            setCurrentRun(created);
            void queueAnalysisSave({
                active_stage: "runs",
                run_phase: "running",
                source: pipelineSource,
                cases,
                run: created,
                report: null,
            });
            timers.current = [
                setTimeout(() => setRunPhase("running"), 450),
                setTimeout(() => setRunPhase("healing"), 1400),
                setTimeout(() => void finishPipelineRun(created.run_id), 2400),
            ];
        } catch {
            startRun();
            setActive("runs");
        }
    }

    async function finishPipelineRun(runId: string) {
        let completed: TestRun | null = null;
        try {
            completed = await completePageTestRun(runId);
            setCurrentRun(completed);
        } catch {
            completed = currentRun ? { ...currentRun, status: "PASSED" } : null;
            if (completed) setCurrentRun(completed);
        } finally {
            setRunPhase("passed");
            if (pipelineSource && completed) {
                void queueAnalysisSave({
                    active_stage: "overview",
                    run_phase: "passed",
                    source: pipelineSource,
                    cases,
                    run: completed,
                    report: { overview, case_count: cases.length, completed_at: new Date().toISOString() },
                });
            }
        }
    }

    async function refreshAgents() {
        const result = await loadAgents();
        setAgents(result.data);
    }

    if (authChecking || currentUser === null) {
        return <LoginView checking={authChecking} error={authError} onLogin={handleLogin} />;
    }

    const initials = currentUser.display_name.slice(0, 2).toUpperCase();
    const activeLabel = active === "integrations" ? "集成与模型" : navItems.find((item) => item.key === active)?.label;

    return (
        <div className="app-shell">
            <aside className="sidebar">
                <div className="brand">
                    <div className="brand-mark">Q</div>
                    <div>
                        <strong>QAScope</strong>
                        <span>Agent Platform</span>
                        <small className="company-name">点泽智能科技</small>
                    </div>
                </div>
                <div className="project-switcher">
                    <span className="project-logo">S</span>
                    <div>
                        <small>当前项目</small>
                        <strong>智测商城 · UAT</strong>
                    </div>
                    <button aria-label="切换项目">⌄</button>
                </div>
                <nav aria-label="主导航">
                    <p>工作台</p>
                    {navItems
                        .filter((item) => item.key !== "browser" || currentUser.roles.includes("ADMIN"))
                        .map((item) => (
                            <button className={active === item.key ? "active" : ""} key={item.key} onClick={() => navigateTo(item.key)}>
                                <span>{item.glyph}</span>
                                {item.label}
                                {item.key === "runs" && <i>7</i>}
                            </button>
                        ))}
                    <p>质量闭环</p>
                    <button>
                        <span>◫</span>缺陷中心<i className="warning-count">12</i>
                    </button>
                    <button>
                        <span>⌁</span>质量洞察
                    </button>
                    <p>平台管理</p>
                    {currentUser.roles.includes("ADMIN") && (
                        <button className={active === "integrations" ? "active" : ""} onClick={() => setActive("integrations")}>
                            <span>◇</span>集成与模型
                        </button>
                    )}
                    <button>
                        <span>⚙</span>平台设置
                    </button>
                </nav>
                <div className="sidebar-foot">
                    <div className="avatar">{initials}</div>
                    <div>
                        <strong>{currentUser.display_name}</strong>
                        <span>{currentUser.roles.join(" / ")}</span>
                    </div>
                    <button aria-label="退出登录" onClick={() => void handleLogout()}>
                        退出
                    </button>
                </div>
            </aside>
            <main className="workspace">
                <header className="topbar">
                    <div className="breadcrumb">
                        <span>智测商城</span>
                        <b>/</b>
                        <strong>{activeLabel}</strong>
                    </div>
                    <div className="top-actions">
                        <span className={`data-source ${source}`}>● {source === "live" ? "后端已连接" : "演示数据"}</span>
                        {pipelineSource && (
                            <span className={`analysis-save-state ${saveState}`}>
                                {saveState === "saving"
                                    ? "○ 保存中"
                                    : saveState === "saved"
                                      ? "✓ 已保存"
                                      : saveState === "error"
                                        ? "! 未保存"
                                        : "○ 待保存"}
                            </span>
                        )}
                        <button aria-label="搜索">⌕</button>
                        <button className="notification" aria-label="通知">
                            ♢<i />
                        </button>
                        <div className="account-menu" ref={accountMenuRef}>
                            <button
                                aria-expanded={accountMenuOpen}
                                aria-haspopup="menu"
                                aria-label="打开账户菜单"
                                className="avatar compact account-menu-trigger"
                                onClick={() => setAccountMenuOpen((open) => !open)}
                                title={currentUser.username}
                            >
                                {initials}
                            </button>
                            {accountMenuOpen && (
                                <div aria-label="账户菜单" className="account-menu-popover" role="menu">
                                    <div className="account-menu-user">
                                        <strong>{currentUser.display_name}</strong>
                                        <span>{currentUser.username}</span>
                                        <small>{currentUser.roles.join(" / ")}</small>
                                    </div>
                                    <button className="account-menu-logout" onClick={() => void handleLogout()} role="menuitem">
                                        <span aria-hidden="true">↪</span>
                                        退出登录
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>
                <div className="content">
                    {["browser", "generate", "runs", "overview"].includes(active) && (
                        <TestPipelineStrip
                            active={active}
                            caseCount={cases.length}
                            hasReport={currentRun?.status === "PASSED"}
                            onNavigate={navigateTo}
                            runPhase={runPhase}
                            source={pipelineSource}
                        />
                    )}
                    {active === "overview" && (
                        <OverviewView
                            caseCount={cases.length}
                            overview={overview}
                            run={currentRun}
                            runPhase={runPhase}
                            onStartRun={startRun}
                            onOpenGenerator={() => navigateTo("generate")}
                            source={pipelineSource}
                        />
                    )}
                    {active === "orders" && <OrdersView csrfToken={csrfToken} roles={currentUser.roles} />}
                    {active === "generate" && (
                        <GeneratorView
                            cases={cases}
                            isGenerating={isGenerating}
                            key={`${pipelineSource?.target_url ?? "manual"}:${pipelineSource?.requirement ?? "default"}`}
                            onExecute={() => void executeGeneratedCases()}
                            onGenerate={(requirement) => void generateCases(requirement)}
                            source={pipelineSource}
                        />
                    )}
                    {active === "browser" && (
                        <BrowserLabView csrfToken={csrfToken} onCreateScenarios={acceptPageAnalysis} restoredSource={pipelineSource} />
                    )}
                    {active === "runs" && (
                        <RunsView
                            onOpenOverview={() => navigateTo("overview")}
                            onStart={() => (pipelineSource && cases.length ? void executeGeneratedCases() : startRun())}
                            phase={runPhase}
                            run={currentRun}
                            source={pipelineSource}
                        />
                    )}
                    {active === "agents" && <AgentsView agents={agents} />}
                    {active === "integrations" && <IntegrationsView csrfToken={csrfToken} onAgentSaved={refreshAgents} />}
                </div>
            </main>
        </div>
    );
}

function authErrorMessage(error: unknown): string {
    if (!(error instanceof ApiRequestError)) return "登录失败，请稍后重试";
    if (error.errorCode === "INVALID_CREDENTIALS") return "用户名或密码错误";
    if (error.errorCode === "VALIDATION_ERROR") return "用户名格式或密码长度不符合要求";
    if (error.errorCode === "SESSION_INVALID" || error.errorCode === "AUTH_REQUIRED") return "登录状态已失效，请重新登录";
    return error.message;
}

function fallbackPipelineRun(source: PageTestSource, generated: GeneratedCase[], status: TestRun["status"]): TestRun {
    return {
        run_id: `RUN-LOCAL-${Date.now()}`,
        status,
        environment: "uat",
        priority: "P0",
        case_ids: generated.map((item) => item.case_id),
        source_url: source.target_url,
        requirement: source.requirement,
        agent_id: "agent-sh-01",
        lease_expires_at: new Date(Date.now() + 5 * 60_000).toISOString(),
        events: [],
        healing: null,
        artifacts: [],
        jira_key: null,
    };
}
